/**
 * Command API - Handles command queue operations for IoT devices
 *
 * This API integrates with the backend command system that uses TPM-based authentication
 * and supports various command types like reverse SSH, reboots, config updates, etc.
 *
 * Backend API endpoints are RESTful and follow the pattern:
 * - GET /api/v1/commands/devices/{deviceId}/commands - List device commands
 * - POST /api/v1/commands/devices/{deviceId}/commands - Queue new command
 * - GET /api/v1/commands/{commandId} - Get command details
 * - PATCH /api/v1/commands/{commandId}/status - Update command status
 * - DELETE /api/v1/commands/{commandId} - Cancel command
 */

import { baseApi } from './baseApi';
import type { Command, CommandCatalog, PaginatedResponse, ApiResponse } from '../../types/api';

/** Query filters for fetching commands */
interface CommandFilters {
  /** Required: Device ID to filter commands for */
  deviceId?: string;
  /** Optional: Filter by command status (pending, in_progress, completed, failed, cancelled) */
  status?: Command['status'];
  /** Optional: Filter by command type */
  type?: Command['type'];
  /** Optional: Search by command_type or created_by */
  search?: string;
  /** Page number for pagination (default: 1) */
  page?: number;
  /** Number of items per page (default: 20) */
  pageSize?: number;
  /** Field to sort by (createdAt, status, command_type, etc.) */
  sortBy?: string;
  /** Sort order (asc or desc) */
  sortOrder?: 'asc' | 'desc';
}

/** Request to queue a command for a specific device - matches backend CommandRequest schema */
interface QueueDeviceCommandRequest {
  /** Type of command (setup_ssh_tunnel, reboot, update_config, etc.) */
  command_type: string;
  /** Command-specific parameters */
  parameters?: Record<string, unknown>;
  /** Command priority (1-10, higher = more important) */
  priority?: number;
  /** Command timeout in seconds (30-3600) */
  timeout?: number;
}

export const commandApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get paginated list of commands for a specific device
     *
     * ✅ WORKING - Uses backend endpoint: GET /api/v1/commands/devices/{deviceId}/commands
     *
     * Note: deviceId is required as commands are always device-specific in the current backend
     */
    getCommands: builder.query<PaginatedResponse<Command>, CommandFilters>({
      query: (filters) => {
        if (filters.deviceId) {
          return {
            url: `/commands/devices/${filters.deviceId}/commands`,
            params: {
              status: filters.status,
              search: filters.search,
              page: filters.page || 1,
              per_page: filters.pageSize || 20,
            },
          };
        } else {
          throw new Error('DeviceId is required for fetching commands');
        }
      },
      // Transform the backend response to match frontend expectations
      transformResponse: (response: any) => {
        // Backend returns {commands, total, page, per_page}
        // Frontend expects {data, total, page, pageSize, totalPages}
        const commands = (response.commands || []).map((cmd: any) => ({
          // Map backend fields to frontend fields
          id: cmd.command_id,
          command_id: cmd.command_id,
          deviceId: cmd.device_id,
          device_id: cmd.device_id,
          type: cmd.command_type,
          command_type: cmd.command_type,
          command: cmd.command_type, // Use command_type as command display
          status: cmd.status,
          parameters: cmd.parameters,
          result: cmd.result,
          error: cmd.error,
          createdAt: cmd.created_at,
          created_at: cmd.created_at,
          updatedAt: cmd.updated_at,
          updated_at: cmd.updated_at,
          completedAt: cmd.completed_at,
          completed_at: cmd.completed_at,
          startedAt: cmd.started_at,
          priority: cmd.priority,
          timeout: cmd.timeout,
          output: cmd.output,
          createdBy: cmd.created_by || 'admin', // Default to admin if not provided
        }));

        return {
          data: commands,
          total: response.total || 0,
          page: response.page || 1,
          pageSize: response.per_page || 20,
          totalPages: Math.ceil((response.total || 0) / (response.per_page || 20)),
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Command' as const, id })),
              { type: 'Command', id: 'LIST' },
            ]
          : [{ type: 'Command', id: 'LIST' }],
    }),

    /**
     * Queue command for a specific device
     *
     * ✅ WORKING - Uses backend endpoint: POST /api/v1/commands/devices/{deviceId}/commands
     *
     * This is the primary way to send commands to devices
     */
    queueDeviceCommand: builder.mutation<{command_id: string}, {deviceId: string; command: QueueDeviceCommandRequest}>({
      query: ({ deviceId, command }) => ({
        url: `/commands/devices/${deviceId}/commands`,
        method: 'POST',
        body: command,
      }),
      invalidatesTags: [
        { type: 'Command', id: 'LIST' },
        { type: 'Device', id: 'LIST' },
      ],
    }),

    /**
     * Cancel a pending command
     *
     * ✅ WORKING - Uses backend endpoint: DELETE /api/v1/commands/{id}
     *
     * Only works for commands with status 'pending'
     */
    cancelCommand: builder.mutation<ApiResponse<Command>, string>({
      query: (id) => ({
        url: `/commands/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Command', id },
        { type: 'Command', id: 'LIST' },
      ],
    }),

    /**
     * Retry a failed command
     *
     * ❌ NEEDS IMPLEMENTATION - Backend endpoint not available
     * Expected: POST /api/v1/commands/{id}/retry
     */
    retryCommand: builder.mutation<ApiResponse<Command>, string>({
      query: (id) => ({
        url: `/commands/${id}/retry`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Command', id: 'LIST' }],
    }),

    /**
     * Get the Command Catalog: every command type and its parameters.
     *
     * ✅ WORKING - Uses backend endpoint: GET /api/v1/commands/supported-types
     *
     * The server serves `contracts/command-catalog.json` verbatim, keyed by command
     * type. Filter on `operator_issuable` before offering a type to a human.
     */
    getCommandCatalog: builder.query<CommandCatalog, void>({
      query: () => '/commands/supported-types',
      providesTags: ['Command'],
    }),
  }),
});

export const {
  useGetCommandsQuery,
  useQueueDeviceCommandMutation,
  useCancelCommandMutation,
  useRetryCommandMutation,
  useGetCommandCatalogQuery,
} = commandApi;
